// Single source of truth for the post-withdraw3 "active bridge" handoff
// state shared between `useInternalTransfer` (writer), the background
// watcher `useHyperliquidBridgeWatcher` (consumer), and the
// stranded-withdraw banner (fallback consumer).

const STORAGE_KEY = 'hl-active-bridge'
const LOCK_KEY = 'hl-active-bridge-lock'

// After this window the watcher gives up and the stranded-recovery banner
// becomes the user's path. Both producers honor the same threshold so
// they never both show their UI at the same time.
export const ACTIVE_BRIDGE_STALE_MS = 30 * 60 * 1000

// Lock TTL — if a tab dies mid-bridge, another tab can reclaim the lock
// after this many ms without the held tab refreshing it.
const LOCK_TTL_MS = 30_000

export type ActiveBridgeState = {
	startedAt: number
	destination: string
	destChainId: number
	destCurrency: string
	destRecipient: string
	arbBaselineRaw: string
	expectedArbAmountRaw: string
	amountFormatted: string
}

export function readActiveBridge(): ActiveBridgeState | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return null
		return JSON.parse(raw) as ActiveBridgeState
	} catch {
		return null
	}
}

export function persistActiveBridge(state: ActiveBridgeState): void {
	if (typeof window === 'undefined') return
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
	} catch {}
}

export function clearActiveBridge(): void {
	if (typeof window === 'undefined') return
	try {
		localStorage.removeItem(STORAGE_KEY)
		localStorage.removeItem(LOCK_KEY)
	} catch {}
}

export function isActiveBridgeFresh(state: ActiveBridgeState | null): boolean {
	return Boolean(state && Date.now() - state.startedAt < ACTIVE_BRIDGE_STALE_MS)
}

// Cross-tab lock. localStorage is shared across tabs of the same origin;
// two mounted watchers would otherwise both fire the Relay leg.
// Not strictly atomic (no CAS in localStorage), but the read-after-write
// verification catches the common race where two tabs claim within the
// same animation frame.
export function tryClaimWatcherLock(tabId: string): boolean {
	if (typeof window === 'undefined') return false
	try {
		const existing = localStorage.getItem(LOCK_KEY)
		if (existing) {
			// Treat corrupt JSON as "no live lock" — otherwise a malformed
			// value would block all claims forever until the user clears
			// storage. parsedExisting === null means we fall through to a
			// fresh claim below.
			let parsedExisting: { tabId?: string; claimedAt?: number } | null = null
			try {
				parsedExisting = JSON.parse(existing) as { tabId?: string; claimedAt?: number }
			} catch {
				parsedExisting = null
			}
			if (parsedExisting?.claimedAt && Date.now() - parsedExisting.claimedAt < LOCK_TTL_MS) {
				return parsedExisting.tabId === tabId
			}
		}
		localStorage.setItem(LOCK_KEY, JSON.stringify({ tabId, claimedAt: Date.now() }))
		const verify = localStorage.getItem(LOCK_KEY)
		if (!verify) return false
		const parsed = JSON.parse(verify) as { tabId?: string }
		return parsed?.tabId === tabId
	} catch {
		return false
	}
}

export function refreshWatcherLock(tabId: string): void {
	if (typeof window === 'undefined') return
	try {
		const existing = localStorage.getItem(LOCK_KEY)
		if (!existing) return
		const parsed = JSON.parse(existing) as { tabId?: string }
		if (parsed?.tabId !== tabId) return
		localStorage.setItem(LOCK_KEY, JSON.stringify({ tabId, claimedAt: Date.now() }))
	} catch {}
}

export function releaseWatcherLock(tabId: string): void {
	if (typeof window === 'undefined') return
	try {
		const existing = localStorage.getItem(LOCK_KEY)
		if (!existing) return
		const parsed = JSON.parse(existing) as { tabId?: string }
		if (parsed?.tabId !== tabId) return
		localStorage.removeItem(LOCK_KEY)
	} catch {}
}

// Returns true if the given tabId currently owns a fresh lock. Called
// from the watcher between critical steps — if the stranded-recovery
// banner cleared the active-bridge state while the watcher was awaiting
// the next step, the lock is gone and the watcher should bail out
// instead of duplicating the Relay leg.
export function isWatcherLockOwned(tabId: string): boolean {
	if (typeof window === 'undefined') return false
	try {
		const existing = localStorage.getItem(LOCK_KEY)
		if (!existing) return false
		const parsed = JSON.parse(existing) as { tabId?: string; claimedAt?: number }
		if (!parsed?.claimedAt || Date.now() - parsed.claimedAt >= LOCK_TTL_MS) return false
		return parsed.tabId === tabId
	} catch {
		return false
	}
}
